import type { components } from '@tallerya/api-client';
import type { ProblemaApi } from '@tallerya/i18n';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { useTallerDeLaCache } from '../../taller/ProveedorTaller';

// Lo que Configuraciones › Catálogos (#154) le pide a la API: las categorías de
// productos y servicios, las especialidades y los tipos de activo con sus campos
// (#117, que antes tenían pantalla propia).
//
// **Cada catálogo se trae entero y se filtra en la pantalla.** Son listas
// cortas —un taller tiene decenas de categorías, no miles—, y así la columna de
// la izquierda dice cuántos tiene cada uno sin pedirlos otra vez.
//
// **Toda escritura de un campo manda la versión del tipo**, no la del campo
// (#93): es lo que hace que dos personas cambiando campos a la vez choquen en
// vez de mezclarse. Por eso, después de cualquier escritura —salga bien o mal—
// se vuelve a pedir el tipo: la versión siguiente sale de ahí.

export type TipoEnLista = components['schemas']['AssetTypeListItem'];
export type Tipo = components['schemas']['AssetTypeDetail'];
export type Campo = components['schemas']['AssetTypeFieldItem'];
export type TipoDeDato = components['schemas']['AssetFieldType'];

/// El taller de la pestaña (#205), en toda clave de caché: regla 5 de multitenancy.
function useTaller(): string {
  return useTallerDeLaCache();
}

// Comparte la raíz con la lista que usa Marcas y modelos: un tipo nuevo o
// renombrado tiene que verse también en su filtro.
const claves = {
  todo: (taller: string) => ['tipos-de-activo', taller] as const,
  ficha: (taller: string, id: string) => ['tipos-de-activo', taller, 'ficha', id] as const,
  // Bajo `catalogo`, la raíz de Productos/Servicios: un cambio acá refresca su
  // combo y su filtro de categorías.
  categorias: (taller: string) => ['catalogo', taller, 'categorias', 'todas'] as const,
  raizCatalogo: (taller: string) => ['catalogo', taller] as const,
  especialidades: (taller: string) => ['especialidades', taller] as const,
};

/** La versión llega como `number | string` —un `uint` en el contrato—. Se manda como número. */
export const version = (v: number | string) => Number(v);

/** `habilitado` en falso no pide nada: para quien no tiene permiso de verlos. */
export function useTipos(habilitado = true) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.todo(taller),
    enabled: habilitado,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/asset-types');
      if (error || !data) throw new Error('No se pudieron leer los tipos de activo');
      return data.items;
    },
  });
}

export function useTipo(id: string | undefined) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.ficha(taller, id ?? ''),
    enabled: id !== undefined,
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/asset-types/{id}', {
        params: { path: { id: id ?? '' } },
      });
      // El 404 no es un fallo de red: el tipo no existe o es de otro taller.
      if (response.status === 404) return null;
      if (error || !data) throw new Error('No se pudo leer el tipo de activo');
      return data;
    },
    // Siempre fresco: con la versión vieja en caché, la próxima escritura
    // chocaría contra un cambio que ya está en la base.
    staleTime: 0,
  });
}

export interface ValoresTipo {
  name: string;
  identifierLabel: string;
  requiresIdentifier: boolean;
}

/** Una opción de lista en edición. `value` solo si ya existía: conserva lo que guardan los activos. */
export interface OpcionEnEdicion {
  label: string;
  value?: string;
}

/**
 * El problema de una escritura, o nada si salió bien. Decide la respuesta y no el
 * cuerpo: un error sin cuerpo —un 500 vacío— llega con `error` indefinido y no
 * puede pasar por éxito.
 */
function fallo(error: unknown, response: Response): ProblemaApi | undefined {
  if (response.ok) return undefined;
  return error && typeof error === 'object' ? (error as ProblemaApi) : {};
}

/**
 * Las escrituras. Cada una devuelve el `ProblemDetails` o nada, la forma que
 * espera `useFormulario`, y **siempre** invalida los tipos del taller: con o sin
 * error, la versión que hay que mandar la próxima vez puede haber cambiado.
 */
export function useCambiosTipos() {
  const taller = useTaller();
  const cache = useQueryClient();

  async function tras<T extends { error?: unknown; response: Response }>(
    peticion: Promise<T>,
  ): Promise<ProblemaApi | undefined> {
    const { error, response } = await peticion;
    await cache.invalidateQueries({ queryKey: claves.todo(taller) });
    return fallo(error, response);
  }

  return {
    /** `alCrear` recibe el id del tipo nuevo, para abrirlo y agregarle campos. */
    crear: async (v: ValoresTipo, alCrear?: (id: string) => void) => {
      const { data, error, response } = await api.POST('/api/asset-types', {
        body: {
          name: v.name.trim(),
          identifierLabel: v.identifierLabel.trim(),
          requiresIdentifier: v.requiresIdentifier,
        },
      });
      if (data) alCrear?.(String(data.id));
      await cache.invalidateQueries({ queryKey: claves.todo(taller) });
      return fallo(error, response);
    },

    editar: (id: string, versionActual: number, v: ValoresTipo) =>
      tras(
        api.PUT('/api/asset-types/{id}', {
          params: { path: { id } },
          body: {
            version: versionActual,
            name: v.name.trim(),
            identifierLabel: v.identifierLabel.trim(),
            requiresIdentifier: v.requiresIdentifier,
          },
        }),
      ),

    cambiarEstado: (id: string, activo: boolean) =>
      tras(api.PUT('/api/asset-types/{id}/status', { params: { path: { id } }, body: { isActive: activo } })),

    agregarCampo: (tipo: string, versionActual: number, etiqueta: string, dato: TipoDeDato, obligatorio: boolean, opciones: string[]) =>
      tras(
        api.POST('/api/asset-types/{id}/fields', {
          params: { path: { id: tipo } },
          body: {
            version: versionActual,
            label: etiqueta.trim(),
            valueType: dato,
            isRequired: obligatorio,
            options: dato === 'list' ? opciones.map((o) => o.trim()) : null,
          },
        }),
      ),

    editarCampo: (
      tipo: string,
      campo: string,
      versionActual: number,
      etiqueta: string,
      obligatorio: boolean,
      opciones: OpcionEnEdicion[] | null,
    ) =>
      tras(
        api.PUT('/api/asset-types/{id}/fields/{fieldId}', {
          params: { path: { id: tipo, fieldId: campo } },
          body: {
            version: versionActual,
            label: etiqueta.trim(),
            isRequired: obligatorio,
            options: opciones?.map((o) => ({ label: o.label.trim(), value: o.value ?? null })) ?? null,
          },
        }),
      ),

    cambiarEstadoCampo: (tipo: string, campo: string, versionActual: number, activo: boolean) =>
      tras(
        api.PUT('/api/asset-types/{id}/fields/{fieldId}/status', {
          params: { path: { id: tipo, fieldId: campo } },
          body: { version: versionActual, isActive: activo },
        }),
      ),

    reordenar: (tipo: string, versionActual: number, campos: string[]) =>
      tras(
        api.PUT('/api/asset-types/{id}/fields/order', {
          params: { path: { id: tipo } },
          body: { version: versionActual, fieldIds: campos },
        }),
      ),
  };
}

// ------------------------------------------------------------ Categorías y especialidades

export type Categoria = components['schemas']['CatalogCategoryListItem'];
export type Especialidad = components['schemas']['SpecialtyListItem'];

/** Todas, activas e inactivas, con cuántos ítems las usan y si se pueden eliminar. */
export function useCategorias(habilitado: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.categorias(taller),
    enabled: habilitado,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/catalog-categories', {
        params: { query: { includeInactive: true } },
      });
      if (error || !data) throw new Error('No se pudieron leer las categorías');
      return data.items;
    },
  });
}

/**
 * Todas, recorriendo las páginas: la API las da de a cien como mucho, y la
 * pantalla las filtra y las cuenta en el navegador.
 */
export function useEspecialidades(habilitado: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.especialidades(taller),
    enabled: habilitado,
    queryFn: async () => {
      const todas: Especialidad[] = [];
      let cursor: string | undefined;
      do {
        const { data, error } = await api.GET('/api/specialties', {
          params: { query: { includeInactive: true, limit: 100, ...(cursor && { cursor }) } },
        });
        if (error || !data) throw new Error('No se pudieron leer las especialidades');
        todas.push(...data.items);
        cursor = data.nextCursor ?? undefined;
      } while (cursor);
      return todas;
    },
  });
}

/**
 * Las escrituras de categorías y especialidades. Cada una devuelve el
 * `ProblemDetails` o nada, la forma que espera `useFormulario`, y **siempre**
 * vuelve a pedir su catálogo: la versión que hay que mandar la próxima vez puede
 * haber cambiado.
 */
export function useCambiosCatalogos() {
  const taller = useTaller();
  const cache = useQueryClient();

  async function tras<T extends { error?: unknown; response: Response }>(
    peticion: Promise<T>,
    clave: readonly unknown[],
  ): Promise<ProblemaApi | undefined> {
    const { error, response } = await peticion;
    await cache.invalidateQueries({ queryKey: clave });
    return fallo(error, response);
  }

  const categorias = claves.raizCatalogo(taller);
  const especialidades = claves.especialidades(taller);

  return {
    crearCategoria: (nombre: string) =>
      tras(api.POST('/api/catalog-categories', { body: { name: nombre.trim() } }), categorias),

    renombrarCategoria: (id: string, versionActual: number, nombre: string) =>
      tras(
        api.PUT('/api/catalog-categories/{id}', {
          params: { path: { id } },
          body: { version: versionActual, name: nombre.trim() },
        }),
        categorias,
      ),

    cambiarEstadoCategoria: (id: string, activa: boolean) =>
      tras(
        api.PUT('/api/catalog-categories/{id}/status', { params: { path: { id } }, body: { isActive: activa } }),
        categorias,
      ),

    eliminarCategoria: (id: string) =>
      tras(api.DELETE('/api/catalog-categories/{id}', { params: { path: { id } } }), categorias),

    crearEspecialidad: (nombre: string) =>
      tras(api.POST('/api/specialties', { body: { name: nombre.trim() } }), especialidades),

    renombrarEspecialidad: (id: string, versionActual: number, nombre: string) =>
      tras(
        api.PUT('/api/specialties/{id}', {
          params: { path: { id } },
          body: { version: versionActual, name: nombre.trim() },
        }),
        especialidades,
      ),

    cambiarEstadoEspecialidad: (id: string, activa: boolean) =>
      tras(api.PUT('/api/specialties/{id}/status', { params: { path: { id } }, body: { isActive: activa } }), especialidades),

    eliminarEspecialidad: (id: string) =>
      tras(api.DELETE('/api/specialties/{id}', { params: { path: { id } } }), especialidades),
  };
}

/** Para buscar sin que importen las tildes ni las mayúsculas: «suspension» encuentra «Suspensión». */
export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}
